import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { WeeklyPickup } from "../components/WeeklyPickup";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Minus, ArrowUp, ArrowDown, Loader2, RefreshCw,
} from "lucide-react";
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, Cell,
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;

const MONTH_OPTIONS = [
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" }, { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" }, { value: 5, label: "Mayo" }, { value: 6, label: "Junio" },
  { value: 7, label: "Julio" }, { value: 8, label: "Agosto" }, { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" }, { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
];

const paceColor = (status) => {
  if (status === "ahead") return "#10b981";
  if (status === "on_track") return "#3b82f6";
  if (status === "behind") return "#f59e0b";
  if (status === "far_behind") return "#ef4444";
  return "#94a3b8";
};

const paceLabel = (status) => {
  if (status === "ahead") return "Mejor que año anterior";
  if (status === "on_track") return "Igual que año anterior";
  if (status === "behind") return "Por debajo";
  if (status === "far_behind") return "Muy por debajo";
  return "Sin datos";
};

const PickupReport = () => {
  const [yearlyPace, setYearlyPace] = useState(null);
  const [pickup, setPickup] = useState(null);
  const [year] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [paceRes, pickupRes] = await Promise.all([
        axios.get(`${API}/api/analytics/yearly-pace?year=${year}`, { headers }),
        axios.get(`${API}/api/analytics/pickup-report?year=${year}&month=${selectedMonth}`, { headers }),
      ]);
      setYearlyPace(paceRes.data);
      setPickup(pickupRes.data);
    } catch (e) {
      toast.error("Error cargando Pickup Report");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, selectedMonth]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveSnapshot = async () => {
    try {
      await axios.post(`${API}/api/analytics/save-snapshot`, {}, { headers });
      toast.success("Snapshot guardado");
      await fetchAll();
    } catch (e) {
      toast.error("Error guardando snapshot");
    }
  };

  const aheadCount = yearlyPace?.months.filter((m) => m.status === "ahead").length || 0;
  const behindCount = yearlyPace?.months.filter((m) => m.status === "behind" || m.status === "far_behind").length || 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pickup-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Pickup & Pace Report</h1>
          <p className="text-muted-foreground">Evolución de reservas vs año anterior</p>
        </div>
        <Button onClick={saveSnapshot} variant="outline" data-testid="save-snapshot-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Guardar snapshot
        </Button>
      </div>

      <WeeklyPickup defaultYear={year + 1} />

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-2 border-emerald-200 bg-emerald-50">
              <CardContent className="pt-4">
                <p className="text-xs text-emerald-700 font-semibold uppercase">Meses por encima</p>
                <p className="text-3xl font-bold text-emerald-600 mt-1" data-testid="months-ahead">{aheadCount} / 12</p>
                <p className="text-xs text-emerald-700 mt-1">vs año anterior</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-red-200 bg-red-50">
              <CardContent className="pt-4">
                <p className="text-xs text-red-700 font-semibold uppercase">Meses por debajo</p>
                <p className="text-3xl font-bold text-red-600 mt-1" data-testid="months-behind">{behindCount} / 12</p>
                <p className="text-xs text-red-700 mt-1">requieren atención</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="pt-4">
                <p className="text-xs text-blue-700 font-semibold uppercase">Snapshots guardados</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">{pickup?.snapshots_count || 0}</p>
                <p className="text-xs text-blue-700 mt-1">para análisis pickup</p>
              </CardContent>
            </Card>
          </div>

          {/* Yearly pace chart */}
          <Card>
            <CardHeader>
              <CardTitle>Pace mensual {year} vs {year - 1}</CardTitle>
              <CardDescription>Comparación mes a mes de Producción</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={yearlyPace?.months || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month_name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => `€${Number(v).toLocaleString("es-ES")}`} />
                  <Legend />
                  <Bar dataKey="previous" name={`${year - 1}`} fill="#94a3b8" />
                  <Bar dataKey="current" name={`${year}`} radius={[6, 6, 0, 0]}>
                    {(yearlyPace?.months || []).map((m, idx) => (
                      <Cell key={`cell-${idx}`} fill={paceColor(m.status)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardHeader>
              <CardTitle>Detalle mes a mes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-2">Mes</th>
                      <th className="text-right p-2">{year}</th>
                      <th className="text-right p-2">{year - 1}</th>
                      <th className="text-right p-2">Diferencia</th>
                      <th className="text-right p-2">%</th>
                      <th className="text-center p-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(yearlyPace?.months || []).map((m) => (
                      <tr key={m.month} className="border-b hover:bg-muted/30" data-testid={`pace-row-${m.month}`}>
                        <td className="p-2 font-medium">{m.month_name}</td>
                        <td className="text-right p-2 font-mono">€{m.current.toLocaleString("es-ES", { maximumFractionDigits: 0 })}</td>
                        <td className="text-right p-2 font-mono text-muted-foreground">€{m.previous.toLocaleString("es-ES", { maximumFractionDigits: 0 })}</td>
                        <td className={`text-right p-2 font-mono font-semibold ${m.diff >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {m.diff >= 0 ? "+" : ""}€{m.diff.toLocaleString("es-ES", { maximumFractionDigits: 0 })}
                        </td>
                        <td className={`text-right p-2 font-bold ${m.diff_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {m.diff_pct >= 0 ? "+" : ""}{m.diff_pct.toFixed(1)}%
                        </td>
                        <td className="text-center p-2">
                          <Badge style={{ backgroundColor: paceColor(m.status), color: "white" }}>
                            {paceLabel(m.status)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Pickup for selected month */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Pickup mensual</CardTitle>
                  <CardDescription>Evolución de reservas día a día</CardDescription>
                </div>
                <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
                  <SelectTrigger className="w-48" data-testid="pickup-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTH_OPTIONS.map((m) => (
                      <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {(!pickup || pickup.snapshots_count === 0) ? (
                <p className="text-center text-muted-foreground py-12">
                  Aún no hay snapshots guardados. Los snapshots se crean automáticamente cada día a las 9:10 AM, o pulsa "Guardar snapshot" para crear el primero.
                </p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={pickup.timeline}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => `€${Number(v).toLocaleString("es-ES")}`} />
                      <Legend />
                      <Area type="monotone" dataKey="production_revenue" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} name="Producción €" />
                      <Area type="monotone" dataKey="billing_revenue" stroke="#10b981" fill="#10b981" fillOpacity={0.2} name="Facturación €" />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                    {pickup.delta_7d && (
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="pt-3 pb-3">
                          <p className="text-xs text-blue-700 font-semibold">Pickup 7 días</p>
                          <p className={`text-xl font-bold ${pickup.delta_7d.production_diff >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                            {pickup.delta_7d.production_diff >= 0 ? "+" : ""}€{pickup.delta_7d.production_diff.toLocaleString("es-ES", { maximumFractionDigits: 0 })}
                          </p>
                        </CardContent>
                      </Card>
                    )}
                    {pickup.delta_30d && (
                      <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="pt-3 pb-3">
                          <p className="text-xs text-purple-700 font-semibold">Pickup 30 días</p>
                          <p className={`text-xl font-bold ${pickup.delta_30d.production_diff >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                            {pickup.delta_30d.production_diff >= 0 ? "+" : ""}€{pickup.delta_30d.production_diff.toLocaleString("es-ES", { maximumFractionDigits: 0 })}
                          </p>
                        </CardContent>
                      </Card>
                    )}
                    <Card className="bg-emerald-50 border-emerald-200">
                      <CardContent className="pt-3 pb-3">
                        <p className="text-xs text-emerald-700 font-semibold">Producción actual</p>
                        <p className="text-xl font-bold text-emerald-700">€{pickup.current_production.toLocaleString("es-ES", { maximumFractionDigits: 0 })}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-orange-50 border-orange-200">
                      <CardContent className="pt-3 pb-3">
                        <p className="text-xs text-orange-700 font-semibold">vs {year - 1}</p>
                        <p className={`text-xl font-bold ${pickup.pace_vs_previous_year_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {pickup.pace_vs_previous_year_pct >= 0 ? "+" : ""}{pickup.pace_vs_previous_year_pct.toFixed(1)}%
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default PickupReport;
